import { PlusIcon, UserX, Users } from "lucide-react";

import { Button } from "~/components/ui/button";
import { CardHeader, CardTitle } from "~/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { ChangePasswordDialog } from "~/pages/admin/ChangePasswordDialog";
import { CreateUserDialog } from "~/pages/admin/CreateUserDialog";
import { DeactivateUserDialog } from "~/pages/admin/DeactivateUserDialog";
import { DeleteUserDialog } from "~/pages/admin/DeleteUserDialog";
import { EditUserDialog } from "~/pages/admin/EditUserDialog";
import { UserTable } from "~/pages/admin/UserTable";
import { useUsers } from "~/pages/admin/useUsers";

const AdminSettings = () => {
  const {
    profiles,
    loading,
    isDialogOpen,
    setIsDialogOpen,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isDeactivateDialogOpen,
    isChangePasswordDialogOpen,
    setIsChangePasswordDialogOpen,
    currentProfile,
    profileToDelete,
    profileToDeactivate,
    deactivateReason,
    setDeactivateReason,
    deactivateLoading,
    profileToChangePassword,
    deleteLoading,
    changePasswordLoading,
    selectedProfiles,
    formData,
    handleInputChange,
    handleRoleChange,
    handleSubmit,
    handleEdit,
    handleDeactivate,
    handleReactivate,
    confirmDeactivate,
    closeDeactivateDialog,
    handleChangePassword,
    confirmDelete,
    confirmChangePassword,
    closeDeleteDialog,
    closeChangePasswordDialog,
    toggleProfileSelection,
    toggleAllProfiles,
    handleUpdateProfile,
    resetForm,
  } = useUsers();

  // Filter active and inactive users
  const activeProfiles = profiles.filter((profile) => profile.is_active);
  const inactiveProfiles = profiles.filter((profile) => !profile.is_active);

  return (
    <div className="max-w-[90vw] px-4 py-6 md:max-w-[70vw]">
      <div className="flex flex-row">
        <CardHeader>
          <CardTitle>Gestión de Usuarios</CardTitle>
        </CardHeader>

        <div className="flex items-center justify-end gap-2">
          <Button onClick={() => setIsDialogOpen(true)}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuevo Usuario
          </Button>
        </div>
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Usuarios Activos ({activeProfiles.length})
          </TabsTrigger>
          <TabsTrigger value="inactive" className="flex items-center gap-2">
            <UserX className="h-4 w-4" />
            Usuarios Desactivados ({inactiveProfiles.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          <UserTable
            profiles={activeProfiles}
            loading={loading}
            onEdit={handleEdit}
            onDeactivate={handleDeactivate}
            onReactivate={handleReactivate}
            onChangePassword={handleChangePassword}
            selectedProfiles={selectedProfiles}
            onToggleSelection={toggleProfileSelection}
            onToggleAll={toggleAllProfiles}
          />
        </TabsContent>

        <TabsContent value="inactive">
          <UserTable
            profiles={inactiveProfiles}
            loading={loading}
            onEdit={handleEdit}
            onDeactivate={handleDeactivate}
            onReactivate={handleReactivate}
            onChangePassword={handleChangePassword}
            selectedProfiles={selectedProfiles}
            onToggleSelection={toggleProfileSelection}
            onToggleAll={toggleAllProfiles}
          />
        </TabsContent>
      </Tabs>

      <CreateUserDialog
        isOpen={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}
        formData={formData}
        onInputChange={handleInputChange}
        onRoleChange={handleRoleChange}
        onSubmit={handleSubmit}
      />

      <EditUserDialog
        isOpen={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) resetForm();
        }}
        formData={formData}
        onInputChange={handleInputChange}
        onRoleChange={handleRoleChange}
        onSubmit={handleUpdateProfile}
      />

      <DeleteUserDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={closeDeleteDialog}
        profile={profileToDelete}
        onConfirm={confirmDelete}
        isDeleting={deleteLoading}
      />

      <DeactivateUserDialog
        isOpen={isDeactivateDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDeactivateDialog();
        }}
        profile={profileToDeactivate}
        reason={deactivateReason}
        onReasonChange={setDeactivateReason}
        onConfirm={confirmDeactivate}
        isDeactivating={deactivateLoading}
      />

      <ChangePasswordDialog
        isOpen={isChangePasswordDialogOpen}
        onOpenChange={closeChangePasswordDialog}
        profile={profileToChangePassword}
        onSubmit={confirmChangePassword}
        isChanging={changePasswordLoading}
      />
    </div>
  );
};

export default AdminSettings;
